import { useCallback } from 'react';
import { useFormContext, UseFormReturn } from 'react-hook-form';
import { LabelValueAcceptedBy, LabelValueSource } from 'graphql/types';
import { useLabelsFormFieldContext } from 'modules/timesheet-labels';

import { useFeatureFlag } from 'modules/common/hooks/useFeatureFlag';
import { shouldAcceptSuggestion } from 'modules/timesheet-labels/hooks';
import {
  PCPTVariantType,
  DependantLabelData,
  DependantLabels
} from '../components/schema';

const setLabelValue = (
  label: DependantLabelData,
  setValue: UseFormReturn['setValue'],
  formField: string,
  isTimeSignalFederatedSuggestionsEnabled: boolean,
  isEmptyLabelsEnabled = false
) => {
  setValue(
    `${formField}.${label.index}.label`,
    {
      ...label.label,
      ...(isTimeSignalFederatedSuggestionsEnabled && {
        value: shouldAcceptSuggestion(label.label, isEmptyLabelsEnabled)
          ? label.label.suggestedValues?.[0]
          : label?.label?.value
      }),
      acceptedBy: LabelValueAcceptedBy.User,
      valueSource: label.label.valueSource ?? LabelValueSource.User
    },
    { shouldValidate: true, shouldDirty: true }
  );
};
const getProgramClientValue = (
  client: DependantLabelData | undefined,
  program: DependantLabelData | undefined
) => {
  if (client) {
    return client;
  } else if (program) {
    return program;
  } else return;
};

export const useAcceptCptSuggestion = (
  dependentLabels: DependantLabels,
  labelType: PCPTVariantType
): (() => void) => {
  const { setValue } = useFormContext();
  const {
    timesheetLabels: timesheetLabelsFormField
  } = useLabelsFormFieldContext();
  const { client, project, task, program } = dependentLabels;
  const programClientValue = getProgramClientValue(client, program);
  const isTimeSignalFederatedSuggestionsEnabled = useFeatureFlag(
    'isTimeSignalFederatedSuggestionsEnabled',
    false
  );
  const isEmptyLabelsEnabled = useFeatureFlag('isEmptyLabelsEnabled', false);
  return useCallback(() => {
    switch (labelType) {
      case 'task':
        setLabelValue(
          task,
          setValue,
          timesheetLabelsFormField,
          isTimeSignalFederatedSuggestionsEnabled,
          isEmptyLabelsEnabled
        );
        setLabelValue(
          project,
          setValue,
          timesheetLabelsFormField,
          isTimeSignalFederatedSuggestionsEnabled,
          isEmptyLabelsEnabled
        );
        programClientValue &&
          setLabelValue(
            programClientValue,
            setValue,
            timesheetLabelsFormField,
            isTimeSignalFederatedSuggestionsEnabled,
            isEmptyLabelsEnabled
          );
        break;
      case 'project':
        setLabelValue(
          project,
          setValue,
          timesheetLabelsFormField,
          isTimeSignalFederatedSuggestionsEnabled,
          isEmptyLabelsEnabled
        );
        programClientValue &&
          setLabelValue(
            programClientValue,
            setValue,
            timesheetLabelsFormField,
            isTimeSignalFederatedSuggestionsEnabled,
            isEmptyLabelsEnabled
          );
        break;
      case 'client':
        client &&
          setLabelValue(
            client,
            setValue,
            timesheetLabelsFormField,
            isTimeSignalFederatedSuggestionsEnabled,
            isEmptyLabelsEnabled
          );
        break;
      case 'program':
        program &&
          setLabelValue(
            program,
            setValue,
            timesheetLabelsFormField,
            isTimeSignalFederatedSuggestionsEnabled,
            isEmptyLabelsEnabled
          );
    }
  }, [
    labelType,
    task,
    setValue,
    timesheetLabelsFormField,
    isTimeSignalFederatedSuggestionsEnabled,
    isEmptyLabelsEnabled,
    project,
    programClientValue,
    client,
    program
  ]);
};
